<template>
  <div class="layout">
    <aside class="sidebar">
      <h1 class="sidebar-brand">会员管理后台</h1>

      <nav class="nav">
        <section v-for="group in groups" :key="group.key" class="nav-group">
          <button
            type="button"
            class="nav-group-btn"
            :class="{ open: isOpen(group.key) }"
            @click="toggle(group.key)"
          >
            <span class="nav-title">{{ group.title }}</span>
            <span class="chevron" aria-hidden="true">▾</span>
          </button>

          <Transition name="collapse">
            <div v-show="isOpen(group.key)" class="nav-items">
              <router-link
                v-for="item in group.items"
                :key="item.to"
                :to="item.to"
                class="nav-link"
                active-class=""
                exact-active-class=""
                :class="{ 'router-link-active': matchesMenuItem(item.to, route.path) }"
              >
                <span class="nav-link-label">{{ item.label }}</span>
                <span
                  v-if="item.badgeKey === 'messages' && unreadCount > 0"
                  class="nav-badge"
                  :title="`待办 ${unreadCount} 条`"
                >{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
              </router-link>
            </div>
          </Transition>
        </section>
      </nav>

      <div class="sidebar-foot">
        <p class="tip">图片与接口经当前站点同源代理（勿直连本机 8081）</p>
      </div>
    </aside>
    <main class="content">
      <header class="content-header">
        <TagsView class="content-header-tags" />
        <div
          class="user-chip"
          @mouseenter="userMenuOpen = true"
          @mouseleave="userMenuOpen = false"
        >
          <div class="user-chip-main">
            <img
              v-if="avatarSrc"
              class="user-avatar"
              :src="avatarSrc"
              alt=""
              @error="onAvatarError"
            />
            <span v-else class="user-avatar user-avatar--ph">{{ avatarLetter }}</span>
            <span class="user-chip-text" :title="userLabel">{{ userLabel }}</span>
            <span class="user-chip-caret">▾</span>
          </div>
          <div v-show="userMenuOpen" class="user-dropdown">
            <button type="button" class="user-dropdown-item" @click="openChangePassword">修改密码</button>
            <button type="button" class="user-dropdown-item user-dropdown-item--danger" @click="onLogout">退出登录</button>
          </div>
        </div>
      </header>
      <div class="content-body">
        <router-view v-slot="{ Component, route: viewRoute }">
          <keep-alive :max="15">
            <component :is="Component" v-if="Component" :key="viewRoute.path" />
          </keep-alive>
        </router-view>
      </div>
    </main>
    <ChangePasswordDialog v-model="showChangePassword" />
    <AdminNotificationStack />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { adminLogout, setAdminToken, toMediaUrl } from '@/api'
import AdminNotificationStack from '@/components/layout/AdminNotificationStack.vue'
import ChangePasswordDialog from '@/components/layout/ChangePasswordDialog.vue'
import TagsView from '@/components/layout/TagsView.vue'
import { useTagsView } from '@/composables/useTagsView'
import { useAdminNotifications } from '@/composables/useAdminNotifications'
import { getAdminPermissions } from '@/utils/adminPermission'
import { disconnectAdminNotificationStream } from '@/utils/adminNotificationStream.js'

const { unreadCount } = useAdminNotifications()

function getAdminProfile() {
  try {
    const raw = localStorage.getItem('admin.profile') || ''
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const profile = ref(getAdminProfile())
const avatarBroken = ref(false)
const userMenuOpen = ref(false)
const showChangePassword = ref(false)

const permissions = computed(() => getAdminPermissions())
const isSuperAdmin = computed(() => permissions.value.includes('*'))
const canRbacManage = computed(() => isSuperAdmin.value || permissions.value.includes('rbac.manage'))

const roleNamesText = computed(() => {
  const names = Array.isArray(profile.value?.roleNames) ? profile.value.roleNames : []
  return names.map((n) => String(n || '').trim()).filter(Boolean).join('、')
})
const displayName = computed(() => profile.value?.displayName || profile.value?.username || '当前账号')
const userLabel = computed(() => {
  const roles = roleNamesText.value
  return roles ? `${roles} · ${displayName.value}` : displayName.value
})
const avatarSrc = computed(() => {
  if (avatarBroken.value) return ''
  return toMediaUrl(profile.value?.avatarUrl || '')
})
const avatarLetter = computed(() => {
  const s = String(displayName.value || '').trim()
  return s ? s.slice(0, 1) : '用'
})

const groups = computed(() => [
  {
    key: 'home',
    title: '首页',
    items: [
      { label: '资讯管理', to: '/content/articles-v2' },
      { label: '相册', to: '/cms/home/albums' },
    ],
  },
  {
    key: 'activity',
    title: '活动',
    items: [
      { label: '活动管理', to: '/content/activities' },
      { label: '问卷管理', to: '/content/satisfaction-forms' },
    ],
  },
  {
    key: 'member',
    title: '会员',
    items: [
      { label: '会员管理', to: '/member/manage' },
      { label: '全部用户', to: '/member/map-profiles' },
      { label: '会员活跃度', to: '/member/engagement' },
      { label: '人脉订单', to: '/member/network-orders' },
      { label: '跨组织合作', to: '/member/cooperation' },
      { label: '模板管理', to: '/member/templates' },
      { label: '入会表单管理', to: '/member/join-form' },
      { label: '服务登记管理', to: '/member/service-forms' },
    ],
  },
  {
    key: 'supply',
    title: '供需',
    items: [
      { label: '供需审核', to: '/supply/review' },
      { label: '合作撮合', to: '/supply/intents' },
    ],
  },
  {
    key: 'message',
    title: '消息管理',
    items: [
      { label: '消息中心', to: '/operation/messages/center', badgeKey: 'messages' },
      { label: '消息发送记录', to: '/operation/messages/records' },
      { label: '被动消息管理', to: '/operation/messages/passive' },
    ],
  },
  {
    key: 'system',
    title: '系统',
    items: [
      { label: '数据看板', to: '/dashboard' },
      { label: '字典管理', to: '/system/dict' },
      { label: '操作日志', to: '/system/operation-logs' },
      ...(canRbacManage.value ? [{ label: '权限管理', to: '/system/permissions' }] : []),
    ],
  },
])

const STORAGE_KEY = 'admin.sidebar.openGroups.v1'
const route = useRoute()
const router = useRouter()
const { syncFromRoute, ensureAffix, resetTagsView } = useTagsView()
const openGroups = ref({})

let removeAfterEach = null

function safeParse(json, fallback) {
  try {
    return JSON.parse(json)
  } catch {
    return fallback
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(openGroups.value))
  } catch {
    // ignore
  }
}

function isOpen(key) {
  return openGroups.value?.[key] === true
}

function toggle(key) {
  openGroups.value = { ...openGroups.value, [key]: !isOpen(key) }
  persist()
}

function onAvatarError() {
  avatarBroken.value = true
}

function openChangePassword() {
  userMenuOpen.value = false
  showChangePassword.value = true
}

async function onLogout() {
  userMenuOpen.value = false
  disconnectAdminNotificationStream()
  try {
    await adminLogout()
  } catch {
    // ignore logout api error and still clear local session
  }
  resetTagsView()
  setAdminToken('')
  localStorage.removeItem('admin.profile')
  router.replace('/login')
}

function matchesMenuItem(to, path) {
  const p = path || ''
  const t = to || ''
  if (!t) return false
  if (p === t) return true
  if (p.startsWith(`${t}/`)) return true
  if (t === '/cms/home' && p.startsWith('/cms/home')) return true
  if (t === '/content/activities' && p.startsWith('/content/activities')) return true
  if (t === '/content/activities' && p.startsWith('/content/activity-registrations')) return true
  if (t === '/content/satisfaction-forms' && p.startsWith('/content/satisfaction-forms')) return true
  if (t === '/content/activities' && p.startsWith('/content/activity-view-logs')) return true
  if (t === '/content/activities' && p.startsWith('/content/activity-fee-orders')) return true
  if (t === '/content/articles-v2' && p.startsWith('/content/articles-v2')) return true
  if (t === '/content/articles' && p.startsWith('/content/articles') && !p.startsWith('/content/articles-v2')) return true
  if (t === '/member/templates' && p.startsWith('/member/templates')) return true
  if (t === '/member/service-forms' && p.startsWith('/member/service-forms')) return true
  if (
    t === '/operation/messages/records' &&
    (p.startsWith('/operation/messages/records') ||
      p.startsWith('/operation/messages/create') ||
      p.startsWith('/operation/messages/detail'))
  )
    return true
  if (t === '/operation/messages/passive' && p.startsWith('/operation/messages/passive')) return true
  if (
    t === '/operation/messages/center' &&
    (p.startsWith('/operation/messages/center') || p === '/system/messages')
  )
    return true
  return false
}

const activeGroupKey = computed(() => {
  const path = route.path || ''
  const hit = groups.value.find((g) => g.items.some((it) => matchesMenuItem(it.to, path)))
  return hit?.key
})

onMounted(() => {
  profile.value = getAdminProfile()
  avatarBroken.value = false
  ensureAffix()
  syncFromRoute(route)
  removeAfterEach = router.afterEach((to) => {
    syncFromRoute(to)
    profile.value = getAdminProfile()
  })

  const saved = safeParse(localStorage.getItem(STORAGE_KEY) || '', null)
  if (saved && typeof saved === 'object') {
    openGroups.value = saved
    if (openGroups.value.operation && !openGroups.value.message) {
      openGroups.value.message = openGroups.value.operation
      delete openGroups.value.operation
    }
  }
  if (activeGroupKey.value) {
    openGroups.value = { ...openGroups.value, [activeGroupKey.value]: true }
    persist()
  }
})

onUnmounted(() => {
  if (removeAfterEach) {
    removeAfterEach()
    removeAfterEach = null
  }
})

watch(
  () => route.path,
  () => {
    if (activeGroupKey.value && !isOpen(activeGroupKey.value)) {
      openGroups.value = { ...openGroups.value, [activeGroupKey.value]: true }
      persist()
    }
  },
)
</script>

<style scoped>
* { box-sizing: border-box; }
.layout {
  height: 100%;
  max-height: 100%;
  display: flex;
  background: #f5f7fb;
  overflow: hidden;
  overscroll-behavior: none;
}
.sidebar {
  width: 240px;
  background: #17212e;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px 12px;
  flex-shrink: 0;
  height: 100%;
  overflow: hidden;
}
.sidebar-brand {
  flex-shrink: 0;
  margin: 0 0 14px;
  font-size: 18px;
}

.nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.nav-group { margin-top: 2px; }

.nav-group-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03));
  color: #e5edf7;
  cursor: pointer;
  user-select: none;
}
.nav-group-btn:hover { background: rgba(255, 255, 255, 0.08); }
.nav-group-btn:active { transform: translateY(0.5px); }

.nav-title {
  font-size: 13px;
  letter-spacing: 0.02em;
  color: #b8c7de;
  font-weight: 800;
}

.chevron {
  font-size: 14px;
  color: #9db0cc;
  transform: rotate(-90deg);
  transition: transform .18s ease;
}
.nav-group-btn.open .chevron { transform: rotate(0deg); }

.nav-items {
  margin-top: 6px;
  padding: 6px 6px 2px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
}

.nav-link {
  color: #e5edf7;
  text-decoration: none;
  margin: 4px 0;
  padding: 9px 10px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: transparent;
  font-size: 14px;
}
.nav-link-label {
  min-width: 0;
  flex: 1;
}
.nav-badge {
  flex-shrink: 0;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #f79009;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}
.nav-link.router-link-active .nav-badge {
  background: #fff;
  color: #2f6af6;
}
.nav-link:hover { background: rgba(255, 255, 255, 0.06); }
.nav-link.router-link-active { background: #2f6af6; color: #fff; }

.collapse-enter-active,
.collapse-leave-active {
  transition: max-height .18s ease, opacity .18s ease, transform .18s ease;
  overflow: hidden;
}
.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-2px);
}
.collapse-enter-to,
.collapse-leave-from {
  max-height: 420px;
  opacity: 1;
  transform: translateY(0);
}

.sidebar-foot { flex-shrink: 0; margin-top: 8px; }
.tip { opacity: .8; font-size: 12px; padding: 8px 12px; margin: 0; }
.content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow: hidden;
  overscroll-behavior: none;
}
.content-header {
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin: -20px -20px 0;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  min-height: 44px;
  z-index: 20;
}
.content-header-tags {
  flex: 1;
  min-width: 0;
}
.user-chip {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 14px 0 8px;
  border-left: 1px solid #f1f5f9;
}
.user-chip-main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 280px;
  cursor: default;
  padding: 6px 4px;
}
.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  background: #e8eef8;
  flex-shrink: 0;
}
.user-avatar--ph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #2563eb;
  background: #eff4ff;
}
.user-chip-text {
  font-size: 13px;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}
.user-chip-caret {
  font-size: 12px;
  color: #98a2b3;
}
.user-dropdown {
  position: absolute;
  top: calc(100% - 4px);
  right: 8px;
  min-width: 140px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
  padding: 6px;
  z-index: 30;
}
.user-dropdown-item {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
}
.user-dropdown-item:hover {
  background: #f8fafc;
  color: #2563eb;
}
.user-dropdown-item--danger:hover {
  background: #fef3f2;
  color: #d92d20;
}
.content-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}
</style>
